import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import type {
    CategoryCardData,
    GovernorateOption,
    HomepageData,
    OfferCardData,
    ProductCardData,
    ShellData
} from '@/types'

import { Bot, MessageCircle, PackageSearch } from 'lucide-react'
import { toLocale, useI18n } from '@/lib/i18n'
import { whatsappLink } from '@/lib/phone'
import { Seo } from '@/components/generals/seo'
import { ButtonLink, buttonClasses } from '@/components/ui/button'
import { StoreLayout } from '@/components/store/store-layout'
import { CmsText } from '@/components/store/cms-text'
import { SectionHeading } from '@/components/store/section-heading'
import { CategoryCard } from '@/components/store/category-card'
import { ProductGrid } from '@/components/store/product-card'
import { OfferCard } from '@/components/store/offer-card'
import { Reveal } from '@/components/store/reveal'
import { ShippingEstimator } from '@/components/store/shipping-estimator'
import { openAssistant } from '@/components/store/assistant-widget'
import { Hero } from '@/components/landing/hero'
import { WholesaleBand } from '@/components/landing/wholesale-band'

import {
    getCategoryCards,
    getGovernorates,
    getHomepageRow,
    getOfferCards,
    listProducts,
    loadOffers,
    loadShell,
    toHomepage
} from '@/lib/server/store'

interface HomeProps {
    shell: ShellData
    homepage: HomepageData
    categories: Array<CategoryCardData>
    featured: Array<ProductCardData>
    offers: Array<OfferCardData>
    governorates: Array<GovernorateOption>
    spotlight: ProductCardData | null
}

const Home: FC<HomeProps> = ({ shell, homepage, categories, featured, offers, governorates, spotlight }): ReactNode => {
    const { tx } = useI18n()
    const { settings } = shell
    const liveOffers = offers.filter(offer => offer.live)
    const shippingSample = governorates.find(governorate => governorate.code === 'cairo') ?? governorates[0] ?? null

    return (
        <StoreLayout shell={shell}>
            <Seo
                description={homepage.heroSubtitle?.v || settings.tagline.v || undefined}
                siteName={settings.storeName.v}
                origin={shell.origin}
                path='/'
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Store',
                    name: settings.storeName.v,
                    description: settings.tagline.v,
                    url: shell.origin || undefined,
                    telephone: settings.phone || settings.whatsapp || undefined,
                    email: settings.email || undefined,
                    areaServed: 'EG',
                    currenciesAccepted: 'EGP'
                }}
            />

            <Hero
                homepage={homepage}
                settings={settings}
                spotlight={spotlight}
                shippingSample={shippingSample}
                liveOffer={liveOffers[0] ?? null}
                governoratesCount={governorates.length}
            />

            {homepage.showCategories && categories.length > 0 && (
                <section id='categories' className='container-page py-16 sm:py-20' aria-labelledby='categories-title'>
                    <Reveal>
                        <SectionHeading
                            id='categories-title'
                            title={<CmsText table='homepage_content' id={homepage.id} field={homepage.categoriesTitle} />}
                            action={{ href: '/products', label: tx('كل المنتجات', 'All products') }}
                        />
                    </Reveal>

                    <div className='mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {categories.map((category, index) => (
                            <Reveal key={category.id} delay={index * 60}>
                                <CategoryCard category={category} className='h-full' />
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {homepage.showFeatured && featured.length > 0 && (
                <section id='featured' className='bg-surface py-16 sm:py-20' aria-labelledby='featured-title'>
                    <div className='container-page'>
                        <Reveal>
                            <SectionHeading
                                id='featured-title'
                                title={<CmsText table='homepage_content' id={homepage.id} field={homepage.featuredTitle} />}
                                subtitle={homepage.featuredSubtitle ? <CmsText table='homepage_content' id={homepage.id} field={homepage.featuredSubtitle} as='p' /> : undefined}
                                action={{ href: '/products', label: tx('تصفح الكل', 'Browse all') }}
                            />
                        </Reveal>

                        <ProductGrid products={featured} className='mt-10' />
                    </div>
                </section>
            )}

            {homepage.showWholesale && <WholesaleBand homepage={homepage} sample={spotlight} />}

            {homepage.showOffers && liveOffers.length > 0 && (
                <section id='offers' className='container-page py-16 sm:py-20' aria-labelledby='offers-title'>
                    <Reveal>
                        <SectionHeading
                            id='offers-title'
                            title={<CmsText table='homepage_content' id={homepage.id} field={homepage.offersTitle} />}
                            subtitle={homepage.offersSubtitle ? <CmsText table='homepage_content' id={homepage.id} field={homepage.offersSubtitle} as='p' /> : undefined}
                            action={{ href: '/offers', label: tx('كل العروض', 'All offers') }}
                        />
                    </Reveal>

                    <div className='mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                        {liveOffers.slice(0, 3).map((offer, index) => (
                            <Reveal key={offer.id} delay={index * 80} className='h-full'>
                                <OfferCard offer={offer} />
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {homepage.showShipping && governorates.length > 0 && (
                <section id='shipping' className='bg-surface py-16 sm:py-20' aria-labelledby='shipping-title'>
                    <div className='container-page grid gap-10 lg:grid-cols-12 lg:items-center'>
                        <Reveal className='flex flex-col gap-5 lg:col-span-5'>
                            <span className='text-sm font-semibold text-cyan-700'>{tx('الشحن والتوصيل', 'Shipping and delivery')}</span>
                            <CmsText table='homepage_content' id={homepage.id} field={homepage.shippingTitle} as='h2' className='text-balance text-3xl font-bold leading-tight tracking-tight text-navy-950 sm:text-4xl' />
                            {homepage.shippingText && (
                                <CmsText table='homepage_content' id={homepage.id} field={homepage.shippingText} as='p' className='text-pretty text-lg leading-relaxed text-muted-foreground' />
                            )}
                            <div>
                                <ButtonLink href='/shipping' variant='outline'>{tx('أسعار الشحن لكل المحافظات', 'Shipping fees for every governorate')}</ButtonLink>
                            </div>
                        </Reveal>

                        <Reveal delay={100} className='lg:col-span-7'>
                            <ShippingEstimator governorates={governorates} freeShippingEnabled={settings.freeShippingEnabled} freeShippingThreshold={settings.freeShippingThreshold} />
                        </Reveal>
                    </div>
                </section>
            )}

            <section id='help' className='container-page py-16 sm:py-20' aria-labelledby='help-title'>
                <Reveal className='relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-navy-900 via-navy-900 to-navy-800 px-6 py-12 text-white sm:px-12 sm:py-14'>
                    <div className='absolute -end-24 -top-24 size-72 rounded-full bg-cyan-400/20 blur-3xl' aria-hidden='true' />

                    <div className='relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex max-w-2xl flex-col gap-3'>
                            <h2 id='help-title' className='text-balance text-3xl font-bold leading-tight tracking-tight sm:text-4xl'>
                                {tx('محتار تختار؟ إحنا معاك', 'Not sure what to pick? We can help')}
                            </h2>
                            <p className='text-pretty text-lg leading-relaxed text-white/70'>
                                {tx('اسأل المساعد الذكي عن أي منتج أو سعر أو عرض أو تكلفة شحن، وهيرد عليك من بيانات المتجر مباشرة.', 'Ask the store assistant about any product, price, offer or shipping fee, and it answers straight from the store data.')}
                            </p>
                        </div>

                        <div className='flex flex-wrap gap-3'>
                            {settings.assistantEnabled && (
                                <button type='button' onClick={openAssistant} className={buttonClasses('accent', 'lg')}>
                                    <Bot className='size-5' aria-hidden='true' />
                                    {tx('اسأل المساعد', 'Ask the assistant')}
                                </button>
                            )}

                            {settings.whatsapp
                                ? (
                                    <a href={whatsappLink(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className={buttonClasses('light', 'lg')}>
                                        <MessageCircle className='size-5 text-[#1fa855]' aria-hidden='true' />
                                        {tx('واتساب', 'WhatsApp')}
                                    </a>
                                )
                                : (
                                    <ButtonLink href='/products' variant='light' size='lg'>
                                        <PackageSearch className='size-5' aria-hidden='true' />
                                        {tx('تصفح المنتجات', 'Browse products')}
                                    </ButtonLink>
                                )}
                        </div>
                    </div>
                </Reveal>
            </section>
        </StoreLayout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async ({ locale: rawLocale, req }) => {
    const locale = toLocale(rawLocale)
    const homepagePromise = getHomepageRow()
    const offersPromise = loadOffers()

    const [homepageRow, offers, shell, categories, governorates] = await Promise.all([
        homepagePromise,
        offersPromise,
        loadShell(locale, req, homepagePromise),
        getCategoryCards(locale),
        getGovernorates(locale)
    ])

    const [featured, offerCards] = await Promise.all([
        listProducts({ featured: true, limit: 8 }, locale, offers),
        getOfferCards(locale, offers, 4)
    ])

    const spotlight = [...featured]
        .filter(product => product.pricing.wholesaleFinal !== null && product.pricing.wholesaleSavingPct > 0)
        .sort((a, b) => b.pricing.wholesaleSavingPct - a.pricing.wholesaleSavingPct)[0] ?? featured[0] ?? null

    return {
        props: {
            shell,
            homepage: toHomepage(homepageRow, locale),
            categories: categories.filter(category => category.productCount > 0),
            featured,
            offers: offerCards,
            governorates,
            spotlight
        }
    }
}

export default Home
